'use client';

import { useEffect, useRef, useState } from 'react';
import { SparkleIcon, XIcon } from '@phosphor-icons/react';
import type { CategoryKind, CategoryRefSource, CreateCustomCategoryRequest, SimilarCategorySuggestion } from '@/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCategorySuggestion } from '@/hooks/useCategorySuggestion';
import { useSimilarCategorySuggestion } from '@/hooks/useSimilarCategorySuggestion';
import { CATEGORY_ICON_NAMES, CategoryIcon } from '@/components/CategoryIcon';
import { ColorSwatchPicker } from '@/components/categories/ColorSwatchPicker';
import { IconPicker } from '@/components/categories/IconPicker';
import { SuggestionCaption } from '@/components/categories/SuggestionCaption';
import {
  DEFAULT_CATEGORY_ICON,
  applySuggestion,
  defaultCategoryColor,
} from '@/components/categories/categoryHelpers';
import { hexWithAlpha } from '@/lib/colors';
import { useChartSeries } from '@/lib/theme/useThemeColors';
import { parseLimitInput } from './helpers';

export interface EditableCategory {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
}

interface NewCategorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saving: boolean;
  onCreate: (data: CreateCustomCategoryRequest, limitAmount: number | null) => Promise<void>;
  /** Edit mode (kebab → Editar categoria): prefills name/cor/ícone; Tipo and limit hide. */
  editing?: EditableCategory | null;
  onUpdate?: (id: string, data: { name: string; icon: string | null; color: string | null }) => Promise<void>;
  /**
   * Accepting the "já existe" AI nudge: points the caller at the existing category
   * instead of creating a duplicate. The callback owns closing the sheet (it may fail to
   * resolve the category and need to keep the sheet open instead).
   */
  onSelectExisting?: (categoryId: string, source: CategoryRefSource) => void;
}

// Desktop = centered modal (mockup .modal-scrim .modal); mobile = bottom sheet, matching
// the dedicated phone mockup.
export function NewCategorySheet({
  open,
  onOpenChange,
  saving,
  onCreate,
  editing = null,
  onUpdate,
  onSelectExisting,
}: NewCategorySheetProps) {
  const isMobile = useIsMobile();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CategoryKind>('expense');
  const [icon, setIcon] = useState<string>(DEFAULT_CATEGORY_ICON);
  const [color, setColor] = useState<string>(() => defaultCategoryColor());
  const [limitDraft, setLimitDraft] = useState('');
  // Per-field touch state, doing double duty: a touched field is off-limits for
  // the AI suggestion, and an edit-save sends the ORIGINAL value for untouched
  // fields (a null icon/color is seeded to a visible default for the pickers, so
  // an untouched save must not silently promote null to the seed). `suggested`
  // drives the sparkle affordance on the preview tile.
  const [dirty, setDirty] = useState({ icon: false, color: false, kind: false });
  const [suggested, setSuggested] = useState(false);

  // "Did you mean an existing category?" duplicate-avoidance nudge.
  const [similar, setSimilar] = useState<SimilarCategorySuggestion | null>(null);
  const [similarDismissedFor, setSimilarDismissedFor] = useState<string | null>(null);

  const isEdit = editing != null;
  // The sheet stays mounted, so local state survives close — re-seed it on every open:
  // edit mode prefills from the target, create mode always starts blank (otherwise a
  // cancelled edit leaks its values into the next "Nova categoria").
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setIcon(editing.icon ?? DEFAULT_CATEGORY_ICON);
      setColor(editing.color ?? defaultCategoryColor());
    } else {
      setName('');
      setKind('expense');
      setIcon(DEFAULT_CATEGORY_ICON);
      setColor(defaultCategoryColor());
      setLimitDraft('');
    }
    setDirty({ icon: false, color: false, kind: false });
    setSuggested(false);
    setSimilar(null);
    setSimilarDismissedFor(null);
  }, [open, editing]);

  // A name edit invalidates the similar-category match found for the PREVIOUS name —
  // otherwise a stale chip could point "Usar essa" at the wrong category after a rename.
  useEffect(() => {
    setSimilar(null);
  }, [name]);

  // One-time ring flash on the controls a suggestion just filled, so the
  // change is attributable instead of fields appearing to move on their own.
  const [flash, setFlash] = useState({ icon: false, color: false, kind: false });
  const flashTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
  }, []);

  const palette = useChartSeries();
  const { loading: suggesting } = useCategorySuggestion({
    enabled: open && !isEdit && !saving,
    name,
    palette,
    onSuggestion: (suggestion) => {
      const patch = applySuggestion(suggestion, dirty, CATEGORY_ICON_NAMES);
      if (patch.icon) setIcon(patch.icon);
      if (patch.color) setColor(patch.color);
      if (patch.kind) setKind(patch.kind);
      if (patch.icon || patch.color || patch.kind) {
        setSuggested(true);
        setFlash({ icon: Boolean(patch.icon), color: Boolean(patch.color), kind: Boolean(patch.kind) });
        if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(
          () => setFlash({ icon: false, color: false, kind: false }),
          1400,
        );
      }
    },
  });
  // The attribution stays up while any suggested field still stands; once the
  // user has overridden everything the chip has nothing left to claim.
  const suggestionApplied = suggested && !(dirty.icon && dirty.color && dirty.kind);

  useSimilarCategorySuggestion({
    enabled: open && !isEdit && !saving,
    name,
    onSuggestion: setSimilar,
  });
  const showSimilarChip = !isEdit && !!similar && similarDismissedFor !== name.trim();
  const handleUseSimilarCategory = () => {
    if (!similar || !onSelectExisting) return;
    // The callback owns closing the sheet (via its own open-state prop) — it may not be
    // able to navigate yet (e.g. taxonomy still loading) and needs to keep the sheet open
    // with the draft intact rather than closing unconditionally and going nowhere.
    onSelectExisting(similar.categoryId, similar.source);
  };
  const handleDismissSimilar = () => setSimilarDismissedFor(name.trim());
  // Inset ring (box-shadow, no layout impact, draws inward): a -m/p spacer
  // widens past the scroll container (horizontal scrollbar) and an outward
  // ring gets flat-clipped at the container's left edge.
  const flashRing = (on: boolean) =>
    `rounded-xl transition-shadow duration-700 ${on ? 'ring-2 ring-accent-brand/60 ring-inset' : ''}`;

  const canSave = name.trim().length > 0 && !saving;
  const limitEnabled = !isEdit && kind === 'expense';

  const handleSave = async () => {
    if (!canSave) return;
    if (isEdit && onUpdate) {
      await onUpdate(editing.id, {
        name: name.trim(),
        icon: dirty.icon ? icon : editing.icon,
        color: dirty.color ? color : editing.color,
      });
    } else {
      const parsed = limitEnabled ? parseLimitInput(limitDraft) : null;
      // 0 now parses as valid (it clears an existing limit) — never CREATE a 0 limit.
      const limit = parsed === 0 ? null : parsed;
      await onCreate({ name: name.trim(), icon, color, kind }, limit);
    }
    setName('');
    setLimitDraft('');
  };

  const body = (
    <div className={`flex-1 space-y-5 overflow-y-auto ${isMobile ? 'px-4 pb-4' : ''}`}>
          <div>
            <div className="flex items-center gap-3">
              <span
                className="grid size-12 shrink-0 place-items-center rounded-xl"
                style={{ backgroundColor: hexWithAlpha(color, 0.12), color }}
              >
                <CategoryIcon name={icon} className="size-6" />
              </span>
              <div className="flex-1">
                <label htmlFor="new-category-name" className="mb-1 block text-xs font-semibold text-text-secondary">Nome</label>
                <input
                  id="new-category-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Cafés & coworking"
                  maxLength={100}
                  className="h-10 w-full rounded-md border border-border-subtle bg-surface px-3 text-base text-text-primary shadow-xs outline-none placeholder:text-text-tertiary focus-visible:border-brand-600 focus-visible:ring-[3px] focus-visible:ring-brand-200/60 sm:text-sm"
                />
              </div>
            </div>
            {!isEdit && (
              <div className="mt-1.5">
                <SuggestionCaption loading={suggesting} applied={suggestionApplied} />
              </div>
            )}
          </div>

          {showSimilarChip && (
            <div className="flex items-center gap-2 rounded-lg border border-border-brand/40 bg-brand-50 px-2.5 py-1.5 text-xs text-brand-800">
              <SparkleIcon className="size-3.5 shrink-0 text-accent-brand" weight="fill" />
              <span className="min-w-0 flex-1">
                Já existe <b>&quot;{similar!.label}&quot;</b>{similar!.parentLabel ? ` em ${similar!.parentLabel}` : ''}.
              </span>
              {onSelectExisting && (
                <button
                  type="button"
                  disabled={saving}
                  className="shrink-0 font-semibold underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={handleUseSimilarCategory}
                >
                  Usar essa
                </button>
              )}
              <button
                type="button"
                aria-label="Ignorar sugestão"
                disabled={saving}
                className="shrink-0 text-text-tertiary hover:text-text-secondary disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleDismissSimilar}
              >
                <XIcon className="size-3.5" />
              </button>
            </div>
          )}

          {!isEdit && (
            <div>
              <p className="mb-1.5 text-xs font-semibold text-text-secondary">Tipo</p>
              <div className={`inline-block ${flashRing(flash.kind)}`}>
              <div className="inline-flex rounded-lg border border-border p-0.5">
                {(
                  [
                    ['expense', 'Despesa'],
                    ['income', 'Receita'],
                    ['neutral', 'Mov. interna'],
                  ] as const
                ).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={kind === k}
                    onClick={() => {
                      setKind(k);
                      setDirty((d) => ({ ...d, kind: true }));
                    }}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                      kind === k ? 'bg-surface text-brand-700 shadow-sm' : 'text-text-secondary'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              </div>
              {kind === 'neutral' && (
                <p className="mt-1.5 text-2xs text-text-tertiary">
                  Movimentação interna: movimenta dinheiro, mas não conta como gasto — ex.:
                  aportes, transferências, pagamento de fatura.
                </p>
              )}
            </div>
          )}

          <div>
            <p className="mb-1.5 text-xs font-semibold text-text-secondary">Cor</p>
            <div className={flashRing(flash.color)}>
              <ColorSwatchPicker
                value={color}
                onChange={(hex) => {
                  setColor(hex);
                  setDirty((d) => ({ ...d, color: true }));
                }}
              />
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold text-text-secondary">Ícone</p>
            <div className={flashRing(flash.icon)}>
              <IconPicker
                value={icon}
                onChange={(i) => {
                  setIcon(i);
                  setDirty((d) => ({ ...d, icon: true }));
                }}
              />
            </div>
          </div>

          {limitEnabled && (
            <div>
              <label htmlFor="new-category-limit" className="mb-1 block text-xs font-semibold text-text-secondary">
                Limite mensal (opcional)
              </label>
              <div className="flex h-10 items-center gap-2 rounded-md border border-border-subtle bg-surface px-3 shadow-xs focus-within:border-brand-600 focus-within:ring-[3px] focus-within:ring-brand-200/60">
                <span className="text-xs text-text-tertiary">R$</span>
                <input
                  id="new-category-limit"
                  value={limitDraft}
                  onChange={(e) => setLimitDraft(e.target.value)}
                  inputMode="decimal"
                  placeholder="0,00"
                  className="flex-1 bg-transparent font-mono text-base tabular-nums outline-none sm:text-sm"
                />
                <span className="text-2xs text-text-tertiary">por mês</span>
              </div>
              <p className="mt-1 text-2xs text-text-tertiary">
                Você pode definir ou mudar o limite depois, direto na linha da categoria.
              </p>
            </div>
          )}
    </div>
  );

  const title = isEdit ? 'Editar categoria' : 'Nova categoria';
  const description = isEdit
    ? 'Nome, cor e ícone — o histórico e as regras não mudam.'
    : 'Aparece junto das demais na lista — só você vê.';

  const actions = (
    <>
      <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
        Cancelar
      </Button>
      <Button disabled={!canSave} onClick={handleSave}>
        {isEdit ? 'Salvar alterações' : 'Criar categoria'}
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="data-[vaul-drawer-direction=bottom]:h-[92dvh]">
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          {body}
          <DrawerFooter className="flex-row justify-end gap-2 border-t border-border-subtle">
            {actions}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] flex-col gap-4 p-5 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter className="flex-row justify-end gap-2 border-t border-border-subtle pt-3">
          {actions}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
